// Competitions — Browse page (UI-SPEC §4/§5, boards B1–B6).
// Data: dashboardService.getCompetitionsBrowse (services/api.js — URL to be
// confirmed against the BE PR). Search is filter-as-you-type over the loaded
// list (B2); regions without a match are absent. "Empty renders nothing":
// missing counts/seasons/flags simply don't render — no placeholders.
//
// Mobile behaviour (declared per CLAUDE.md UI rule 1): header stacks, search
// goes full width, card grids become a single-column list, section counts are
// hidden (B6 — deliberately without counts). Nothing is cut off.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashboardService } from "../../services/api";
import tpIcon from "../../assets/images/tp-icon.png";
import {
  groupCompetitions,
  filterSections,
  countCompetitions,
  competitionMonogram,
  competitionName,
} from "./competitionsUtils";
import "./competitions.css";

// GET /api/competitions/ is peek-paginated (per_page max 100, no total). The
// browse design (§4) shows ALL regions at once with a client-side
// filter-as-you-type, so we page through the endpoint until has_more=false.
// PAGE_CAP is a runaway guard (3000 rows); if it ever trips, the header count
// is suppressed rather than showing a wrong total.
const PER_PAGE = 100;
const PAGE_CAP = 30;

const GlassIcon = ({ active }) => (
  <svg
    className="tpcomp-search-glass"
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    stroke={active ? "#2183FE" : "#94A3B8"}
    strokeWidth="1.4"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="4.6" />
    <path d="M10.6 10.6L14 14" strokeLinecap="round" />
  </svg>
);

const ClearIcon = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M3 3l8 8M11 3l-8 8" stroke="#94A3B8" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const CompetitionCard = ({ comp, onOpen }) => {
  const [logoErrored, setLogoErrored] = useState(false);
  const name = competitionName(comp);
  if (!name) return null;
  const country = comp.country?.name || null;
  const season = comp.cur_season?.year || null;
  const metaText = [country, season].filter(Boolean).join(" · ");
  return (
    <button type="button" className="tpcomp-card" onClick={() => onOpen(comp)}>
      <span className="tpcomp-card-logo" aria-hidden="true">
        {comp.logo && !logoErrored ? (
          <img src={comp.logo} alt="" onError={() => setLogoErrored(true)} />
        ) : (
          competitionMonogram(comp)
        )}
      </span>
      <span className="tpcomp-card-body">
        <span className="tpcomp-card-name">{name}</span>
        {metaText ? (
          <span className="tpcomp-card-meta">
            {/* Image flag (never emoji): TSCountry.logo is the flag asset. */}
            {country && comp.country?.logo ? (
              <span className="tpcomp-card-flag">
                <img
                  src={comp.country.logo}
                  alt=""
                  width={16}
                  height={11}
                  style={{ objectFit: "cover", borderRadius: 1, display: "block" }}
                />
              </span>
            ) : null}
            <span className="tpcomp-card-metatext tpcomp-tnum">{metaText}</span>
          </span>
        ) : null}
      </span>
    </button>
  );
};

const BrowseSkeleton = () => (
  <div className="tpcomp-browse" data-testid="competitions-browse-skeleton">
    <div>
      <div className="tpcomp-skel" style={{ width: 220, height: 26 }} />
      <div className="tpcomp-skel-soft" style={{ width: 300, height: 14, marginTop: 10 }} />
    </div>
    {[0, 1].map((s) => (
      <div className="tpcomp-section" key={s}>
        <div className="tpcomp-skel" style={{ width: 150, height: 12 }} />
        <div className="tpcomp-cardgrid">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div className="tpcomp-skel-card" key={i}>
              <div className="tpcomp-skel" style={{ width: 38, height: 38, flex: "none" }} />
              <div style={{ flex: 1 }}>
                <div className="tpcomp-skel" style={{ width: "70%", height: 13 }} />
                <div className="tpcomp-skel-soft" style={{ width: "45%", height: 11, marginTop: 7 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const CompetitionsBrowse = () => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: false, data: null });
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    const { signal } = controller;
    abortRef.current = controller;
    setState({ loading: true, error: false, data: null });
    try {
      const all = [];
      let page = 1;
      let hasMore = true;
      while (hasMore && page <= PAGE_CAP) {
        // eslint-disable-next-line no-await-in-loop
        const res = await dashboardService.getCompetitionsBrowse({
          page,
          perPage: PER_PAGE,
          signal,
        });
        if (signal.aborted) return;
        if (!res.success) {
          setState({ loading: false, error: true, data: null });
          return;
        }
        const rows = Array.isArray(res.data?.results) ? res.data.results : [];
        all.push(...rows);
        hasMore = Boolean(res.data?.pagination?.has_more) && rows.length > 0;
        page += 1;
      }
      setState({
        loading: false,
        error: false,
        data: { competitions: all, complete: !hasMore },
      });
    } catch (e) {
      if (e?.name === "CanceledError" || e?.name === "AbortError") return;
      setState({ loading: false, error: true, data: null });
    }
  }, []);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const sections = useMemo(
    () => groupCompetitions(state.data?.competitions),
    [state.data],
  );
  // No total in the peek-paginated endpoint: the count is the loaded list —
  // suppressed if the runaway cap ever cut the paging short (never show a
  // wrong number).
  const total = state.data?.complete ? state.data.competitions.length : null;
  const visibleSections = useMemo(
    () => filterSections(sections, query),
    [sections, query],
  );
  const matchCount = useMemo(
    () => countCompetitions(visibleSections),
    [visibleSections],
  );
  const searching = query.trim().length > 0;

  const openCompetition = (comp) => {
    const id = comp.id ?? comp.ts_competition_id ?? comp.competition_id;
    if (id) navigate(`/competitions/${id}`);
  };

  if (state.loading) return <BrowseSkeleton />;

  return (
    <div className="tpcomp-page">
      <div className="tpcomp-browse">
        <div className="tpcomp-browse-header">
          <div>
            <div className="tpcomp-browse-title-row">
              <img className="tpcomp-browse-icon" src={tpIcon} alt="" />
              <h1 className="tpcomp-browse-title">Competitions</h1>
            </div>
            <div className="tpcomp-browse-sub">
              {typeof total === "number" && total > 0 ? (
                <span className="tpcomp-tnum">
                  {`Leagues, cups and international tournaments · ${total} competitions`}
                </span>
              ) : (
                <span>Leagues, cups and international tournaments</span>
              )}
            </div>
          </div>
          {!state.error && (
            <label className="tpcomp-search">
              <GlassIcon active={focused || searching} />
              <input
                type="text"
                value={query}
                placeholder="Filter competitions"
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                aria-label="Filter competitions"
              />
              {searching && (
                <button
                  type="button"
                  className="tpcomp-search-clear"
                  onClick={() => setQuery("")}
                  aria-label="Clear filter"
                >
                  <ClearIcon />
                </button>
              )}
            </label>
          )}
        </div>

        {state.error ? (
          <div className="tpcomp-error" role="alert">
            <div className="tpcomp-error-title">
              Competitions could not be loaded
            </div>
            <div className="tpcomp-error-copy">
              The competition list is temporarily unavailable. Nothing is
              missing from your account.
            </div>
            <div className="tpcomp-error-actions">
              <button type="button" className="tpcomp-retry" onClick={load}>
                Retry
              </button>
              <span className="tpcomp-error-code">ERR-COMP-LIST</span>
            </div>
          </div>
        ) : (
          <>
            {searching && typeof total === "number" && (
              <div className="tpcomp-resultline tpcomp-tnum">
                {/* One text node on purpose: the static-translation template
                    matcher works on whole sentences ("{matches} of {total}
                    competitions match “{query}” …"). */}
                {`${matchCount} of ${total} competitions match “${query.trim()}” · regions without a match are absent`}
              </div>
            )}

            {searching && visibleSections.length === 0 ? (
              <div className="tpcomp-empty">
                <div className="tpcomp-empty-title">
                  {`No competition matches “${query.trim()}”`}
                </div>
                <div className="tpcomp-empty-copy">
                  Try a shorter term, or clear the filter to browse all
                  regions.
                </div>
                <button
                  type="button"
                  className="tpcomp-empty-link"
                  onClick={() => setQuery("")}
                >
                  Clear filter
                </button>
              </div>
            ) : (
              visibleSections.map((section) => (
                <div className="tpcomp-section" key={section.key}>
                  <div className="tpcomp-section-head">
                    <span className="tpcomp-section-title">{section.label}</span>
                    <span className="tpcomp-section-line" />
                    {typeof section.count === "number" && !searching && (
                      <span className="tpcomp-section-count tpcomp-tnum">
                        {`${section.count} competitions`}
                      </span>
                    )}
                  </div>
                  <div className="tpcomp-cardgrid">
                    {section.competitions.map((comp) => (
                      <CompetitionCard
                        key={comp.id ?? comp.ts_competition_id ?? competitionName(comp)}
                        comp={comp}
                        onOpen={openCompetition}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CompetitionsBrowse;
